import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScrollText, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/SEO";
import ToolShell, { Field, inputCls } from "../../components/ToolShell";
import { api } from "../../lib/api";

export default function NumerologyReport() {
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !dob) return toast.error("Please enter your name and date of birth.");
    setLoading(true);
    try {
      const res = await api.post("/tools/report", { full_name: name, dob });
      setData(res.data);
      setTimeout(() => document.getElementById("report-result")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {
      toast.error("Report generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const n = data?.numerology;
  const ai = data?.ai;

  const section = (title, body) => (
    <div className="report-block rounded-2xl border border-border bg-card p-6 shadow-soft">
      <h3 className="font-serif text-xl text-primary">{title}</h3>
      <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</div>
    </div>
  );

  return (
    <ToolShell slug="numerology-report" name="Personalized Report" tagline="Your full life reading, printable">
      <SEO title="Personalized Numerology Report" description="Generate a complete personalized numerology report with your full analysis, remedies, life challenges and a 7-day action plan. Download or print." path="/tools/numerology-report" />

      <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2 max-w-2xl rounded-3xl border border-border bg-card p-7 shadow-soft print:hidden" data-testid="report-form">
        <Field label="Full Name"><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rahul Sharma" data-testid="report-name" /></Field>
        <Field label="Date of Birth"><input type="date" className={inputCls} value={dob} onChange={(e) => setDob(e.target.value)} data-testid="report-dob" /></Field>
        <div className="sm:col-span-2">
          <button disabled={loading} data-testid="report-submit"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScrollText className="h-4 w-4 text-gold" />}
            {loading ? "Crafting your report…" : "Generate my report"}
          </button>
        </div>
      </form>

      {data && (
        <motion.div id="report-result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-14" data-testid="report-result">
          <div className="flex items-center justify-between print:hidden">
            <h2 className="font-serif text-3xl">Your report is ready</h2>
            <button onClick={() => window.print()} data-testid="report-print" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm transition-colors hover:border-primary">
              <Printer className="h-4 w-4" /> Download / Print
            </button>
          </div>

          <div className="mt-6 space-y-4">
            <div className="rounded-3xl bg-primary text-primary-foreground p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-gold">Personalized numerology report</p>
              <h3 className="mt-2 font-serif text-3xl">{n.full_name}</h3>
              <p className="mt-1 text-sm text-primary-foreground/70">{n.title} · Mulank {n.mulank} · Bhagyank {n.bhagyank} · Name No. {n.name_number}</p>
              {ai.summary && <p className="mt-5 text-lg font-light leading-relaxed">{ai.summary}</p>}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {section(`Mulank Analysis (${n.mulank})`, n.personality)}
              {section(`Bhagyank Analysis (${n.bhagyank})`, n.bhagyank_summary)}
              {section("Personality", n.personality)}
              {section("Career", n.career)}
              {section("Love & Marriage", n.relationship)}
              {section("Health", n.health)}
              {section("Finance", n.finance)}
              {section("Strengths", <ul className="space-y-1">{n.strengths.map((s, i) => <li key={i}>• {s}</li>)}</ul>)}
              {section("Weaknesses", <ul className="space-y-1">{n.weaknesses.map((s, i) => <li key={i}>• {s}</li>)}</ul>)}
              {section("Lucky Numbers, Colours & Days", <span>Numbers: {n.lucky_numbers.join(", ")} · Colour: {n.lucky_color} · Day: {n.lucky_day} · Direction: {n.lucky_direction}</span>)}
              {section("Lo Shu Missing Numbers", data.loshu.missing_numbers.length ? data.loshu.missing_numbers.join(", ") : "None — a balanced grid!")}
              {section("Life Challenges", <ul className="space-y-1">{ai.life_challenges.map((s, i) => <li key={i}>• {s}</li>)}</ul>)}
            </div>

            {section("Suggested Remedies (traditional guidance)", <ul className="space-y-1">{ai.remedies.map((s, i) => <li key={i}>• {s}</li>)}</ul>)}

            <div className="rounded-2xl border border-gold/30 bg-gold/10 p-6">
              <h3 className="font-serif text-xl text-primary">7-Day Personalized Action Plan</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {ai.action_plan.map((d, i) => (
                  <div key={i} className="rounded-xl bg-white p-4 border border-border">
                    <p className="text-xs uppercase tracking-[0.15em] text-gold">{d.day} · {d.focus}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{d.action}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-muted-foreground italic">{data.disclaimer}</p>
          </div>
        </motion.div>
      )}

      <section className="mt-20 max-w-3xl print:hidden" data-testid="report-explainer">
        <h2 className="font-serif text-3xl">What is in your personalized numerology report?</h2>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          Enter your full name and date of birth and the tool builds a complete reading you can print or save. It brings your main numbers and your Lo Shu grid into one place.
        </p>

        <h2 className="mt-12 font-serif text-3xl">Sections in the report</h2>
        <ul className="mt-4 space-y-2 text-muted-foreground leading-relaxed">
          <li><strong className="text-foreground">Your numbers:</strong> Mulank, Bhagyank and name number, with a short analysis of each.</li>
          <li><strong className="text-foreground">Personality, career, love and marriage:</strong> traditional themes linked to your numbers.</li>
          <li><strong className="text-foreground">Health and finance:</strong> general reflections only, not medical or financial advice.</li>
          <li><strong className="text-foreground">Strengths and weaknesses:</strong> what tends to come naturally and what may need care.</li>
          <li><strong className="text-foreground">Lucky numbers, colours, days and direction:</strong> traditional associations for your numbers.</li>
          <li><strong className="text-foreground">Lo Shu missing numbers:</strong> the digits that do not appear in your birth date grid.</li>
          <li><strong className="text-foreground">Life challenges and remedies:</strong> themes to be aware of, with simple traditional suggestions.</li>
          <li><strong className="text-foreground">7-day action plan:</strong> one small daily focus and action to try for a week.</li>
        </ul>

        <h2 className="mt-12 font-serif text-3xl">How to use your report</h2>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          Read it as a starting point for reflection. Pick one or two ideas that feel true to you, and try the 7-day plan as a small experiment. Use the Download / Print button to save it as a PDF or print it.
        </p>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          To understand the numbers first, try the <Link to="/tools/numerology-calculator" className="text-primary underline">numerology calculator</Link> and the <Link to="/tools/lo-shu-grid" className="text-primary underline">Lo Shu grid</Link>, or read <Link to="/blog/mulank-vs-bhagyank-what-is-the-difference" className="text-primary underline">Mulank vs Bhagyank</Link>.
        </p>

        <h2 className="mt-12 font-serif text-3xl">Frequently asked questions</h2>
        <div className="mt-6 space-y-6">
          <div>
            <h3 className="font-serif text-xl">Is the personalized report free?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">Yes. You can generate and print your report for free.</p>
          </div>
          <div>
            <h3 className="font-serif text-xl">How do I download my report?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              After the report is ready, tap Download / Print and choose Save as PDF in the print window.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Can the report predict my future?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              No. The report is guidance for reflection and entertainment. It is not a prediction or a guarantee of any outcome.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Can I use the health and finance sections as advice?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              No. These sections are general reflections. For health or money decisions, please speak to a qualified doctor or financial adviser.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Is my information saved?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              Your name and date of birth are sent to an AI service to write your report. We do not save the report on our servers. Please read our <Link to="/privacy" className="text-primary underline">Privacy Policy</Link> for details.
            </p>
          </div>
        </div>
      </section>
    </ToolShell>
  );
}